import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { SumoImageUpload } from "@/components/SumoImageUpload";
import { SumoImageGallery } from "@/components/SumoImageGallery";
import { useSumoImages } from "@/hooks/useSumoImages";
import { Edit, Eye, Trash2, Save, X, Plus } from "lucide-react";
import { Sumo } from "@/hooks/useSumos";

interface SumoEditorProps {
  sumo: Sumo;
  sumoId: string;
  onSave: (sumoData: Partial<Sumo>) => Promise<void>;
  onDelete: (sumoId: string) => Promise<void>;
  onToggleVisibility: (sumoId: string, isVisible: boolean) => Promise<void>;
  isHidden?: boolean;
}

export const SumoEditor = ({ 
  sumo, 
  sumoId, 
  onSave, 
  onDelete, 
  onToggleVisibility,
  isHidden 
}: SumoEditorProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedSumo, setEditedSumo] = useState<Partial<Sumo>>(sumo);
  const { images, refreshImages } = useSumoImages(sumoId);

  const handleSave = async () => {
    try {
      // Assurer que les features sont toujours un array
      const sumoToSave = {
        ...editedSumo,
        features: Array.isArray(editedSumo.features) ? editedSumo.features : []
      };

      await onSave(sumoToSave);
      setIsEditing(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  const handleCancel = () => {
    setEditedSumo(sumo);
    setIsEditing(false);
  };

  const updateField = (field: string, value: any) => {
    setEditedSumo(prev => ({ ...prev, [field]: value }));
  };

  const addFeature = () => {
    const features = Array.isArray(editedSumo.features) ? editedSumo.features : [];
    updateField('features', [...features, '']);
  };

  const updateFeature = (index: number, value: string) => {
    const features = Array.isArray(editedSumo.features) ? editedSumo.features : [];
    const newFeatures = [...features];
    newFeatures[index] = value;
    updateField('features', newFeatures);
  };

  const removeFeature = (index: number) => {
    const features = Array.isArray(editedSumo.features) ? editedSumo.features : [];
    const newFeatures = features.filter((_, i) => i !== index);
    updateField('features', newFeatures);
  };

  return (
    <Card className={`w-full ${isHidden ? 'opacity-60' : ''}`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xl">
          {sumo.title}
          {isHidden && <Badge variant="secondary" className="ml-2">Masqué</Badge>}
        </CardTitle>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onToggleVisibility(sumoId, !sumo.is_visible)}
          >
            <Eye className="w-4 h-4" />
            {sumo.is_visible ? 'Masquer' : 'Afficher'}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm">
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Supprimer le sumo</AlertDialogTitle>
                <AlertDialogDescription>
                  Êtes-vous sûr de vouloir supprimer ce sumo ? Cette action est irréversible.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction onClick={() => onDelete(sumoId)}>
                  Supprimer
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardHeader>

      <CardContent>
        <Tabs defaultValue="details" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="details">Détails</TabsTrigger>
            <TabsTrigger value="images">Images</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-4">
            {!isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Titre</Label>
                    <p className="text-sm font-medium p-2 bg-muted rounded">{sumo.title}</p>
                  </div>
                  <div>
                    <Label>Prix</Label>
                    <p className="text-sm font-medium p-2 bg-muted rounded">{sumo.price}</p>
                  </div>
                </div>

                <div>
                  <Label>Description</Label>
                  <p className="text-sm p-3 bg-muted rounded leading-relaxed">{sumo.description}</p>
                </div>

                <div>
                  <Label>Caractéristiques</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {Array.isArray(sumo.features) && sumo.features.length > 0 ? 
                      sumo.features.map((feature, index) => (
                        <Badge key={index} variant="outline">{feature}</Badge>
                      )) : 
                      <Badge variant="outline">Aucune caractéristique</Badge>
                    }
                  </div>
                </div>

                <div>
                  <Label>Spécifications</Label>
                  <div className="text-sm p-3 bg-muted rounded">
                    {sumo.specifications ? (
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(sumo.specifications).map(([key, value]) => (
                          <div key={key}>
                            <span className="font-medium">{key}:</span> {String(value)}
                          </div>
                        ))}
                      </div>
                    ) : (
                      'Aucune spécification'
                    )}
                  </div>
                </div>

                <Button onClick={() => setIsEditing(true)} className="w-full">
                  <Edit className="w-4 h-4 mr-2" />
                  Modifier les détails
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="title">Titre</Label>
                    <Input
                      id="title"
                      value={editedSumo.title || ''}
                      onChange={(e) => updateField('title', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="price">Prix</Label>
                    <Input
                      id="price"
                      value={editedSumo.price || ''}
                      onChange={(e) => updateField('price', e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={editedSumo.description || ''}
                    onChange={(e) => updateField('description', e.target.value)}
                    rows={3}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <Label>Caractéristiques</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addFeature}>
                      <Plus className="w-4 h-4 mr-1" />
                      Ajouter
                    </Button>
                  </div>
                  <div className="space-y-2 mt-2">
                    {Array.isArray(editedSumo.features) && editedSumo.features.map((feature, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          value={feature}
                          onChange={(e) => updateFeature(index, e.target.value)}
                          placeholder="Caractéristique"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeFeature(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 pt-4 border-t">
                  <Button onClick={handleSave} className="flex-1">
                    <Save className="w-4 h-4 mr-2" />
                    Sauvegarder
                  </Button>
                  <Button variant="outline" onClick={handleCancel} className="flex-1">
                    <X className="w-4 h-4 mr-2" />
                    Annuler
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="images" className="space-y-4">
            <div>
              <Label className="text-base font-semibold">Upload de nouvelles images</Label>
              <SumoImageUpload
                sumoId={sumoId}
                onImagesUploaded={refreshImages}
                maxFiles={10}
              />
            </div>
            
              <div>
                <Label className="text-base font-semibold">Images existantes</Label>
                <SumoImageGallery
                  sumoId={sumoId}
                  onImageDeleted={refreshImages}
                />
              </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};